import { glMatrix, quat, ReadonlyVec3, vec3, vec4 } from "gl-matrix";

import { PositionXYZ } from "../../types/tracking/raw";
import { Builder } from "flatbuffers";
import { Vec3Wire } from "../../types/flatbuffers/mannequin";

glMatrix.setMatrixArrayType(Array);

export function vec3ToFlatBuffer(v: vec3, builder: Builder): number {
  return Vec3Wire.createVec3Wire(builder, v[0], v[1], v[2]);
}

export function vec3FromFlatBuffer(v: Vec3Wire): vec3 {
  return vec3.fromValues(v.x(), v.y(), v.z());
}

export function vec3ToString(v: vec3, precision: number): string {
  let out = "";
  for (let i = 0; i < v.length; i++) {
    const val = v[i];
    out += val.toFixed(precision);
    if (i < v.length -1) out += " ";
  }
  return out;
}

export const vec3Zero: vec3 = vec3.fromValues(0, 0, 0);

export function vec3FromPositionXYZ(position?: PositionXYZ, target?: vec3): vec3 | undefined {
  if (position) {
    target = target ?? vec3.create();
    vec3.set(target, position.x, position.z, -position.y);
    return target;
  } else {
    return undefined;
  }
}

// taken from https://github.com/mrdoob/three.js/blob/master/src/math/Quaternion.js
export function quatFromOrderedEuler(q: quat, euler: ReadonlyVec3, order: string): quat | undefined {
  const x = euler[0];
  const y = euler[1];
  const z = euler[2];

  // http://www.mathworks.com/matlabcentral/fileexchange/
  // 	20696-function-to-convert-between-dcm-euler-angles-quaternions-and-euler-vectors/
  // 	content/SpinCalc.m

  const cos = Math.cos;
  const sin = Math.sin;

  const c1 = cos(x / 2);
  const c2 = cos(y / 2);
  const c3 = cos(z / 2);

  const s1 = sin(x / 2);
  const s2 = sin(y / 2);
  const s3 = sin(z / 2);

  let success = true;
  switch (order.toUpperCase()) {
    case "XYZ":
      q[0] = s1 * c2 * c3 + c1 * s2 * s3;
      q[1] = c1 * s2 * c3 - s1 * c2 * s3;
      q[2] = c1 * c2 * s3 + s1 * s2 * c3;
      q[3] = c1 * c2 * c3 - s1 * s2 * s3;
      break;

    case "YXZ":
      q[0] = s1 * c2 * c3 + c1 * s2 * s3;
      q[1] = c1 * s2 * c3 - s1 * c2 * s3;
      q[2] = c1 * c2 * s3 - s1 * s2 * c3;
      q[3] = c1 * c2 * c3 + s1 * s2 * s3;
      break;

    case "ZXY":
      q[0] = s1 * c2 * c3 - c1 * s2 * s3;
      q[1] = c1 * s2 * c3 + s1 * c2 * s3;
      q[2] = c1 * c2 * s3 + s1 * s2 * c3;
      q[3] = c1 * c2 * c3 - s1 * s2 * s3;
      break;

    case "ZYX":
      q[0] = s1 * c2 * c3 - c1 * s2 * s3;
      q[1] = c1 * s2 * c3 + s1 * c2 * s3;
      q[2] = c1 * c2 * s3 - s1 * s2 * c3;
      q[3] = c1 * c2 * c3 + s1 * s2 * s3;
      break;

    case "YZX":
      q[0] = s1 * c2 * c3 + c1 * s2 * s3;
      q[1] = c1 * s2 * c3 + s1 * c2 * s3;
      q[2] = c1 * c2 * s3 - s1 * s2 * c3;
      q[3] = c1 * c2 * c3 - s1 * s2 * s3;
      break;

    case "XZY":
      q[0] = s1 * c2 * c3 - c1 * s2 * s3;
      q[1] = c1 * s2 * c3 - s1 * c2 * s3;
      q[2] = c1 * c2 * s3 + s1 * s2 * c3;
      q[3] = c1 * c2 * c3 + s1 * s2 * s3;
      break;

    default:
      console.error(`unknown order: ${order}`);
      success = false;
      break;
  }

  return success ? q : undefined;
}
